import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getClientSegmentById,
  editClientSegment,
  getAllCountries,
  getAllSexTypes,
  getAllCitiesWithExistingClients,
  getBrandsFiltered,
  getCategoriesFiltered,
  getAllChannels,
  getClientLevels,
  getClients,
  getAllStatesByCountry,
  deleteClientFromClientSegment,
  addClientsToClientSegment,
  getSegmentClusterComposition,
  getSegmentLinkedSuggestions,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import { formatRecommendationMessage } from "../../helpers/RecommendationMessageHelper";
import { useFetchRequestGuard } from "../../helpers/FilterSearchHelper";

import {
  Form,
  message,
  Select,
  Button,
  Input,
  Table,
  InputNumber,
  Switch,
  DatePicker,
  Tabs,
  Skeleton,
  Empty,
  Modal,
  Popconfirm,
  Tooltip,
  Descriptions,
} from "antd";
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { RxGroup } from "react-icons/rx";
import CustomBreadcrumb from "../../components/breadcrumb/Breadcrumb";
import ActionButtons from "../../components/actionbuttons/ActionButtons";
import ClientSegmentConditionBuilder from "../../components/clientsegmentconditionbuilder/ClientSegmentConditionBuilder";
import { Condition } from "../../types/conditionTypes";
import { mapConditionsToBackendFormat } from "../../helpers/conditionMapper";

import styles from "./ViewClientSegment.module.scss";
import NotFound from "../notfound/NotFound";
import AddArticleModal from "../../components/addarticlemodal/AddArticleModal";
import moment from "moment/moment";

const ViewClientSegment = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const getBasePath = () => {
    if (location.pathname.startsWith('/commercial-intelligence')) {
      return '/commercial-intelligence/segments';
    }
    if (location.pathname.startsWith('/fidelity')) {
      return '/fidelity/clientsegments';
    }
    return '/promotions/clientsegments';
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientSegment, setClientSegment] = useState<any>();
  const [sexTypes, setSexTypes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [statesByCountry, setStatesByCountry] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [articlesSelected, setArticlesSelected] = useState<any[]>([]);
  const [skusSelected, setSkusSelected] = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [clientPage, setClientPage] = useState(1);
  const [totalSizeClients, setTotalSizeClients] = useState(0);
  const [activeTab, setActiveTab] = useState("general");
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [isAddClientsModalVisible, setIsAddClientsModalVisible] = useState(false);
  const [availableClients, setAvailableClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientSearchType, setClientSearchType] = useState<string>("name");
  const [clientSearchTerm, setClientSearchTerm] = useState<string>("");
  const [clientSearchPage, setClientSearchPage] = useState(1);
  const [clientSearchTotal, setClientSearchTotal] = useState(0);
  const [clusterComposition, setClusterComposition] = useState<any[]>([]);
  const [linkedRewards, setLinkedRewards] = useState<any[]>([]);
  const [linkedActivationActions, setLinkedActivationActions] = useState<any[]>([]);
  const [loadingComposition, setLoadingComposition] = useState(true);

  const [infoForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_CLIENTS);

  const { id } = useParams();

  const {
    beginRequest: beginCompositionRequest,
    isStaleRequest: isStaleCompositionRequest,
  } = useFetchRequestGuard();

  const mapBackendDataToConditions = (segment: any): Condition[] => {
    const conditions: Condition[] = [];

    if (segment.sex?.value) {
      conditions.push({
        id: 'sex-' + Date.now(),
        type: 'sex',
        value: segment.sex.value
      });
    }

    if ((segment.averageTicketMin !== undefined && segment.averageTicketMin !== null) ||
        (segment.averageTicketMax !== undefined && segment.averageTicketMax !== null)) {
      conditions.push({
        id: 'averageTicket-' + Date.now(),
        type: 'averageTicket',
        minValue: segment.averageTicketMin,
        maxValue: segment.averageTicketMax
      });
    }

    if (segment.purchaseChannelId && segment.purchaseChannelId.length > 0) {
      conditions.push({
        id: 'channel-' + Date.now(),
        type: 'channel',
        values: segment.purchaseChannelId
      });
    }

    if (segment.minAge !== undefined || segment.maxAge !== undefined) {
      conditions.push({
        id: 'age-' + Date.now(),
        type: 'age',
        minValue: segment.minAge,
        maxValue: segment.maxAge
      });
    }

    if (segment.brands && segment.brands.length > 0) {
      conditions.push({
        id: 'brands-' + Date.now(),
        type: 'brands',
        values: segment.brands.map((brand: any) => brand.id)
      });
    }

    if (segment.categories && segment.categories.length > 0) {
      conditions.push({
        id: 'categories-' + Date.now(),
        type: 'categories',
        values: segment.categories.map((category: any) => category.id)
      });
    }

    if (segment.level?.id) {
      conditions.push({
        id: 'level-' + Date.now(),
        type: 'level',
        value: segment.level.id
      });
    }

    if (
      (segment.purchaseCountRange !== undefined && segment.purchaseCountRange !== null) ||
      (segment.purchaseCountRangeMax !== undefined && segment.purchaseCountRangeMax !== null)
    ) {
      conditions.push({
        id: 'purchaseCount-' + Date.now(),
        type: 'purchaseCount',
        minValue: segment.purchaseCountRange ?? undefined,
        maxValue: segment.purchaseCountRangeMax ?? undefined,
      });
    }

    if (segment.purchaseCountPeriod !== undefined && segment.purchaseCountLastDays !== undefined) {
      conditions.push({
        id: 'purchasesInPeriod-' + Date.now(),
        type: 'purchasesInPeriod',
        value: segment.purchaseCountPeriod,
        minValue: segment.purchaseCountLastDays
      });
    }

    if (segment.purchaseCountStartDate || segment.purchaseCountEndDate) {
      conditions.push({
        id: 'purchasesInDateRange-' + Date.now(),
        type: 'purchasesInDateRange',
        startDate: segment.purchaseCountStartDate,
        endDate: segment.purchaseCountEndDate,
        untilPresent: !segment.purchaseCountEndDate && !!segment.purchaseCountStartDate,
      });
    }

    if (
      (segment.totalSpent !== undefined && segment.totalSpent !== null) ||
      (segment.totalSpentMax !== undefined && segment.totalSpentMax !== null)
    ) {
      conditions.push({
        id: 'totalSpent-' + Date.now(),
        type: 'totalSpent',
        minValue: segment.totalSpent ?? undefined,
        maxValue: segment.totalSpentMax ?? undefined,
      });
    }

    if (segment.birthdayStartDate || segment.birthdayEndDate) {
      conditions.push({
        id: 'birthday-' + Date.now(),
        type: 'birthday',
        startDate: segment.birthdayStartDate ? moment(segment.birthdayStartDate).format('YYYY-MM-DD') : undefined,
        endDate: segment.birthdayEndDate ? moment(segment.birthdayEndDate).format('YYYY-MM-DD') : undefined,
        untilPresent: !segment.birthdayEndDate && !!segment.birthdayStartDate,
      });
    }

    if (segment.programRegistrationStartDate || segment.programRegistrationEndDate) {
      conditions.push({
        id: 'registrationDate-' + Date.now(),
        type: 'registrationDate',
        startDate: segment.programRegistrationStartDate ? moment(segment.programRegistrationStartDate).format('YYYY-MM-DD') : undefined,
        endDate: segment.programRegistrationEndDate ? moment(segment.programRegistrationEndDate).format('YYYY-MM-DD') : undefined,
        untilPresent: !segment.programRegistrationEndDate && !!segment.programRegistrationStartDate,
      });
    }

    if (segment.state?.id) {
      conditions.push({
        id: 'city-' + Date.now(),
        type: 'city',
        value: segment.state.id,
        countryId: segment.state.country?.id
      });
    }

    return conditions;
  };

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      handleFetchClientSegment();
      handleFetchCountries();
      handleFetchSexTypes();
      handleFetchCities();
      handleFetchChannels();
      handleSearchLevels("");
      handleFetchLinkedSuggestions();
    }
  }, []);

  useEffect(() => {
    if (!hasPermission(privilegesEnum.READ_CLIENTS) || !id) {
      return;
    }

    const requestId = beginCompositionRequest();
    setClusterComposition([]);
    setLoadingComposition(true);

    getSegmentClusterComposition(id)
      .then((response) => {
        if (isStaleCompositionRequest(requestId)) return;
        setClusterComposition(response ?? []);
      })
      .finally(() => {
        if (isStaleCompositionRequest(requestId)) return;
        setLoadingComposition(false);
      });

    return () => {
      beginCompositionRequest();
    };
  }, [id, beginCompositionRequest, isStaleCompositionRequest]);

  const handleFetchLinkedSuggestions = async () => {
    const response = await getSegmentLinkedSuggestions(id!);
    setLinkedRewards(response?.rewards ?? []);
    setLinkedActivationActions(response?.activationActions ?? []);
  };

  const handleFetchClientSegment = async (page = clientPage) => {
    setLoading(true);

    let response = await getClientSegmentById(id!, page);
    setArticlesSelected(response?.articles ?? []);
    setSkusSelected(response?.skus ?? []);
    setBrands(response?.brands ?? []);
    setClientSegment(response);
    if (response) {
      infoForm.setFieldsValue({
        name: formatRecommendationMessage(
          t,
          response.nameKey,
          response.nameParams,
          response.name
        ),
        description: formatRecommendationMessage(
          t,
          response.descriptionKey,
          response.descriptionParams,
          response.description
        ),
      });
    }
    setTotalSizeClients(response?.clientsPagination?.totalElements || 0);
    
    if (response && response.type?.value === 'DYNAMIC') {
      const mappedConditions = mapBackendDataToConditions(response);
      setConditions(mappedConditions);
      
      const cityCondition = mappedConditions.find(c => c.type === 'city');
      if (cityCondition && cityCondition.countryId) {
        handleFetchStatesByCountry(cityCondition.countryId);
      }
    } else {
      setConditions([]);
    }
    
    if (response && response.type?.value === 'STATIC') {
      const clientIds = response.clients?.map((client: any) => client.id) || [];
      setSelectedClientIds(clientIds);
    }

    setLoading(false);
  };

  const handlePaginationClients = (page: number) => {
    handleFetchClientSegment(page);
    setClientPage(page);
  };

  const handleFetchChannels = async () => {
    const response = await getAllChannels();
    setChannels(response);
  };

  const handleSearchLevels = async (value: string) => {
    const response = await getClientLevels(0, { name: value });
    setLevels(response.clientLevels);
  };
  const handleSearchBrands = async (value: string) => {
    const response = await getBrandsFiltered(value);
    setBrands(response);
  };

  const handleSearchCategories = async (value: string) => {
    const response = await getCategoriesFiltered(value);
    setCategories(response);
  };

  const handleFetchSexTypes = async () => {
    const response = await getAllSexTypes();
    setSexTypes(response);
  };
  const handleFetchCities: () => any = async () => {
    const response = await getAllCitiesWithExistingClients();

    setCities(response);
  };

  const handleFetchCountries: () => any = async () => {
    const response = await getAllCountries();

    setCountries(response);
  };

  const handleFetchStatesByCountry = async (countryId: number) => {
    if (countryId) {
      const response = await getAllStatesByCountry(countryId);
      setStatesByCountry(response);
    } else {
      setStatesByCountry([]);
    }
  };

  const handleCancelButton = () => {
    const search = location.search;
    navigate(`${getBasePath()}${search}`);
  };

  const getSexTypesOptions = (items: any) => {
    const options: any[] = [];

    items.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.value} key={item.value}>
          {item.description_es}
        </Select.Option>
      );
    });

    return options;
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handleEdit = async () => {
    setSaving(true);

    const data = infoForm.getFieldsValue();

    let response;

    console.log(data);
    console.log(selectedClientIds);
    if (data.type === "STATIC") {
      console.log(selectedClientIds);
      response = await editClientSegment(parseInt(id!), {
        type: "STATIC",
        name: data.name,
        description: data.description ?? "",
        clientIds: selectedClientIds.map(id => id.toString()),
        active: data.active ?? true,
      });
    } else {
      const backendData = mapConditionsToBackendFormat(conditions);
      
      response = await editClientSegment(parseInt(id!), {
        type: "DYNAMIC",
        name: data.name,
        description: data.description ?? "",
        active: data.active ?? true,
        ...backendData,
        skuId: "",
        articleIds: [],
      });
    }

    if (response) {
      message.success(t("Common-EditSuccessMessage"));
      await handleFetchClientSegment();
    } else message.error(t("Common-EditErrorMessage"));
    setSaving(false);
  };

  const handleAddArticle = (articlesToAdd: any) => {
    const newArticles: any[] = [];
    articlesToAdd.forEach((articleToAdd: any) => {
      if (
        articlesSelected.find(
          (article: any) => article.id === articleToAdd.id
        ) === undefined
      )
        newArticles.push(articleToAdd);
    });
    setArticlesSelected([...articlesSelected, ...newArticles]);
  };

  const getChannelOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {`${item.businessUnit?.name} - ${item.type.descriptionEs}`}
        </Select.Option>
      );
    });
    return options;
  };

  const handleRemoveArticle = (articleRemove: any) => {
    const articlesWithoutRemoved = articlesSelected.filter(
      (article) => article.id !== articleRemove.id
    );

    setArticlesSelected(articlesWithoutRemoved);
  };

  const handleAddClients = async (clientsToAdd: any[]) => {
    setIsAddClientsModalVisible(false);
    
    try {
      for (const client of clientsToAdd) {
        const success = await addClientsToClientSegment(parseInt(id!), client.id);
        if (!success) {
          message.error(t("Common-EditErrorMessage"));
          return;
        }
      }
      
      message.success(t("Common-EditSuccessMessage"));
      await handleFetchClientSegment(clientPage);
    } catch (error) {
      message.error(t("Common-EditErrorMessage"));
    }
  };

  const handleOpenAddClientsModal = () => {
    setIsAddClientsModalVisible(true);
    setAvailableClients([]);
    setClientSearchTerm("");
    setClientSearchPage(1);
    setClientSearchTotal(0);
    setClientSearchType("name");
  };

  const handleSearchClients = async (searchTerm: string = "", page: number = 1) => {
    setLoadingClients(true);
    setClientSearchTerm(searchTerm);
    setClientSearchPage(page);
    
    try {
      if (!searchTerm.trim()) {
        setAvailableClients([]);
        setClientSearchTotal(0);
        setLoadingClients(false);
        return;
      }

      const filters: any = {};

      if (clientSearchType === "name") {
        filters.name = searchTerm;
      } else if (clientSearchType === "email") {
        filters.email = searchTerm;
      } else if (clientSearchType === "mobile") {
        filters.mobile = searchTerm;
      } else if (clientSearchType === "birthdate") {
        // Para fecha de nacimiento, usar el mismo día como inicio y fin
        filters.birthStartDate = searchTerm;
        filters.birthEndDate = searchTerm;
      }
      
      const response = await getClients(page, filters);
      const filteredClients = response.clients.filter(
        (client: any) => !selectedClientIds.includes(client.id)
      );
      
      setAvailableClients(filteredClients);
      setClientSearchTotal(response.totalElements);
    } catch (error) {
      console.error("Error searching clients:", error);
      setAvailableClients([]);
      setClientSearchTotal(0);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleClientSearchPagination = (page: number) => {
    handleSearchClients(clientSearchTerm, page);
  };

  const handleRemoveClient = async (clientIdToRemove: number) => {
    try {
      const success = await deleteClientFromClientSegment(parseInt(id!), clientIdToRemove);
      
      if (success) {
        message.success(t("Common-DeleteSuccessMessage"));
        await handleFetchClientSegment(clientPage);
      } else {
        message.error(t("Common-DeleteErrorMessage"));
      }
    } catch (error) {
      message.error(t("Common-DeleteErrorMessage"));
    }
  };

  const getClientColumns = () => {
    const columns: any[] = [
      {
        title: "ID Cliente",
        dataIndex: "id",
        key: "id",
      },
    ];

    if (clientSegment?.type?.value === 'STATIC' && canEdit) {
      columns.push({
        title: t("Common-Actions"),
        key: "actions",
        render: (client: any) => (
          <Tooltip title={t("Common-Delete")}>
            <Popconfirm
              title={t("Common-DeleteConfirm")}
              icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
              onConfirm={() => handleRemoveClient(client.id)}
              okText={t("Common-Yes")}
              cancelText={t("Common-No")}
              okButtonProps={{ danger: true }}
            >
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
                type="text"
              />
            </Popconfirm>
          </Tooltip>
        ),
      });
    }

    return columns;
  };

  const formatCompositionText = (value: any) =>
    value === null || value === undefined || value === "" ? "-" : value;

  const formatCompositionNumber = (value: any) =>
    value === null || value === undefined
      ? "-"
      : Number(value).toLocaleString("es-UY", { maximumFractionDigits: 2 });

  const formatCompositionPercent = (value: any) =>
    value === null || value === undefined
      ? "-"
      : `${Number(value).toLocaleString("es-UY", {
          maximumFractionDigits: 1,
        })}%`;

  const renderClusterComposition = () => {
    if (loadingComposition) {
      return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    if (!clusterComposition || clusterComposition.length === 0) {
      return (
        <Empty
          description={
            <div>
              <div className={styles.emptyTitle}>
                {t("ClientSegment-EmptyComposition")}
              </div>
              <div className={styles.emptyDescription}>
                {t("ClientSegment-EmptyCompositionDescription")}
              </div>
            </div>
          }
        />
      );
    }

    return clusterComposition.map((composition: any, index: number) => (
      <Descriptions
        key={composition.id ?? index}
        bordered
        column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label={t("ClientSegment-Composition-AvgAge")}>
          {formatCompositionNumber(composition.avgAge)}
        </Descriptions.Item>
        <Descriptions.Item label={t("ClientSegment-Composition-PercentWoman")}>
          {formatCompositionPercent(composition.percentWoman)}
        </Descriptions.Item>
        <Descriptions.Item label={t("ClientSegment-Composition-TopBrand")}>
          {formatCompositionText(composition.topBrand)}
        </Descriptions.Item>
        <Descriptions.Item label={t("ClientSegment-Composition-TopCategory")}>
          {formatCompositionText(composition.topCategory)}
        </Descriptions.Item>
        <Descriptions.Item label={t("ClientSegment-Composition-AvgTicketValue")}>
          {formatCompositionNumber(composition.avgTicketValue)}
        </Descriptions.Item>
        <Descriptions.Item
          label={t("ClientSegment-Composition-MedianTicketValue")}
        >
          {formatCompositionNumber(composition.medianTicketValue)}
        </Descriptions.Item>
        <Descriptions.Item label={t("ClientSegment-Composition-AvgTicketCount")}>
          {formatCompositionNumber(composition.avgTicketCount)}
        </Descriptions.Item>
        <Descriptions.Item
          label={t("ClientSegment-Composition-MedianTicketCount")}
        >
          {formatCompositionNumber(composition.medianTicketCount)}
        </Descriptions.Item>
        <Descriptions.Item
          label={t("ClientSegment-Composition-MostFrequentBranch")}
        >
          {formatCompositionText(composition.mostFrequentBranchName)}
        </Descriptions.Item>
      </Descriptions>
    ));
  };

  return loading ? (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <Skeleton.Input style={{ width: 180, height: 20 }} active />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <div
          style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}
        >
          <Skeleton.Button
            style={{ width: 60, height: 20, marginRight: 24 }}
            active
          />
          <Skeleton.Button
            style={{ width: 60, height: 20, marginRight: 24 }}
            active
          />
        </div>
        <div className={styles.clientSegmentInfoWrapper}>
          <Skeleton active paragraph={{ rows: 12 }} />
        </div>
      </div>
    </div>
  ) : !clientSegment ? (
    <NotFound />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxGroup />,
                label: t("Common-ClientSegments"),
                path: getBasePath(),
                search: location.search
              },
              {
                label:
                  formatRecommendationMessage(
                    t,
                    clientSegment?.nameKey,
                    clientSegment?.nameParams,
                    clientSegment?.name
                  ) || t("Common-ClientSegment"),
                isCurrentPage: true
              }
            ]}
          />
          </div>
        <ActionButtons
          onSave={handleEdit}
          onCancel={handleCancelButton}
          loading={saving}
          disabled={saving}
          show={activeTab === "general"}
          permission={privilegesEnum.WRITE_CLIENTS}
        />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className={styles.tabsContainer}
      >
        <Tabs.TabPane tab={t("ClientSegment-Tab-General")} key="general">
          <div className={styles.clientSegmentInfoWrapper}>
          {(linkedRewards.length > 0 || linkedActivationActions.length > 0) && (
            <div style={{ marginBottom: 16 }}>
              {linkedRewards.map((reward) => (
                <div key={`reward-${reward.id}`}>
                  <Link to={`/commercial-intelligence/opportunities/incentives/${reward.id}/view`}>
                    {t("ClientSegment-LinkedReward")}: {reward.name}
                  </Link>
                </div>
              ))}
              {linkedActivationActions.map((action) => (
                <div key={`action-${action.id}`}>
                  <Link
                    to={`/commercial-intelligence/opportunities/activation-actions/${action.id}/view`}
                  >
                    {t("ClientSegment-LinkedActivation")}:{" "}
                    {formatRecommendationMessage(
                      t,
                      action.nameKey,
                      action.nameParams,
                      action.name
                    )}
                  </Link>
                </div>
              ))}
            </div>
          )}
          <Form
            name="basic"
            layout="vertical"
            initialValues={{ remember: true }}
            autoComplete="off"
            className={styles.newForm}
            form={infoForm}
          >
            <Form.Item
              label={t("Common-Name")}
              name="name"
              rules={[
                {
                  required: true,
                  message: t("Common-NameNotEntered"),
                },
              ]}
              initialValue={clientSegment?.name}
            >
              <Input disabled={!canEdit} placeholder={t("Common-Name")} />
            </Form.Item>
            <Form.Item
              initialValue={clientSegment?.description}
              label={t("Common-Description")}
              name="description"
            >
              <Input
                disabled={!canEdit}
                placeholder={t("Common-Description")}
              />
            </Form.Item>
            <Form.Item
              initialValue={clientSegment?.active ?? true}
              name="active"
              hidden
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label={t("Common-Type")}
              name="type"
              rules={[
                {
                  required: true,
                  message: t("Common-Type"),
                },
              ]}
              initialValue={clientSegment?.type.value}
            >
              <Select disabled placeholder={t("Common-Type")} allowClear>
                <Select.Option value="STATIC">Estático</Select.Option>
                <Select.Option value="DYNAMIC">Dinámico</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.type !== currentValues.type
              }
            >
              {({ getFieldValue }) => {
                switch (getFieldValue("type")) {
                  case "DYNAMIC":
                    return (
                      <div className={styles.conditionsSection}>
                        <h3 className={styles.sectionTitle}>{t("Common-Conditions")}</h3>
                        <ClientSegmentConditionBuilder
                          conditions={conditions}
                          onConditionsChange={setConditions}
                          availableOptions={{
                            sexTypes,
                            channels,
                            brands,
                            categories,
                            levels,
                            countries,
                            statesByCountry
                          }}
                          onSearchBrands={handleSearchBrands}
                          onSearchCategories={handleSearchCategories}
                          onSearchLevels={handleSearchLevels}
                          onFetchStatesByCountry={handleFetchStatesByCountry}
                        />
                        <AddArticleModal
                          selectedArticles={articlesSelected}
                          onAddArticle={handleAddArticle}
                          onRemoveArticle={handleRemoveArticle}
                        />
                      </div>
                    );
                  case "STATIC":
                    return (
                      <>
                        {(articlesSelected.length > 0 ||
                          skusSelected.length > 0 ||
                          (brands && brands.length > 0)) && (
                          <div className={styles.conditionsSection}>
                            <h3 className={styles.sectionTitle}>
                              {t("Common-Articles")} / {t("Common-Skus")}
                            </h3>
                            {brands && brands.length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                <strong>{t("Common-Brands")}: </strong>
                                {brands.map((b: any) => b.name).join(", ")}
                              </div>
                            )}
                            {articlesSelected.length > 0 && (
                              <ul>
                                {articlesSelected.map((article: any) => (
                                  <li key={article.id}>
                                    <a href={`/admin/articles/${article.id}/view`}>
                                      {article.name || article.externalId || article.id}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {skusSelected.length > 0 && (
                              <ul>
                                {skusSelected.map((sku: any) => {
                                  const label =
                                    sku.name || sku.externalId || sku.id;
                                  const articleId = sku.articleId;
                                  return (
                                    <li key={sku.id}>
                                      {articleId ? (
                                        <a
                                          href={`/admin/articles/${articleId}/view`}
                                        >
                                          {label}
                                        </a>
                                      ) : (
                                        label
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        )}
                      </>
                    );
                }
              }}
            </Form.Item>
          </Form>
        </div>
        </Tabs.TabPane>

        <Tabs.TabPane tab={t("ClientSegment-Tab-Clients")} key="clients">
          <div className={styles.tableWrapper}>
            <div className={styles.tablesHeader}>
              <h1 className={styles.tableTitle}>{t("Common-Clients")}</h1>
              {clientSegment?.type?.value === 'STATIC' && canEdit && (
                <Button 
                  type="primary" 
                  onClick={handleOpenAddClientsModal}
                  icon={<PlusOutlined />}
                  className={styles.addClientsButton}
                >
                  {t("Common-AddClients")}
                </Button>
              )}
            </div>
            <div>
              {loading || totalSizeClients > 0 ? (
            <Table
              size="small"
              className={styles.table}
                  rowKey={(record) => record.id}
              loading={loading}
              columns={getClientColumns()}
              dataSource={clientSegment?.clients || []}
              pagination={{
                total: totalSizeClients,
                showSizeChanger: false,
                onChange: handlePaginationClients,
                current: clientPage,
                    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
                  }}
                />
              ) : (
                <div className={styles.emptyState}>
                  <Empty
                    description={
                      <div>
                        <div className={styles.emptyTitle}>
                          {t("ClientSegment-EmptyClients")}
                        </div>
                        <div className={styles.emptyDescription}>
                          {t("ClientSegment-EmptyClientsDescription")}
                        </div>
                      </div>
                    }
            />
          </div>
        )}
      </div>
          </div>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={t("ClientSegment-Tab-Composition")}
          key="composition"
        >
          <div className={styles.clientSegmentInfoWrapper}>
            {renderClusterComposition()}
          </div>
        </Tabs.TabPane>
      </Tabs>

      {/* Modal para agregar clientes */}
      <Modal
        title={t("Common-AddClients")}
        visible={isAddClientsModalVisible}
        onCancel={() => setIsAddClientsModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Select
              value={clientSearchType}
              onChange={setClientSearchType}
              style={{ width: 180 }}
            >
              <Select.Option value="name">{t("Common-Name")}</Select.Option>
              <Select.Option value="email">{t("Common-Email")}</Select.Option>
              <Select.Option value="mobile">{t("Common-Cellphone")}</Select.Option>
              <Select.Option value="birthdate">{t("Common-Birthdate")}</Select.Option>
            </Select>
            {clientSearchType === "birthdate" ? (
              <DatePicker
                style={{ flex: 1 }}
                placeholder="Seleccionar fecha"
                format="DD-MM-YYYY"
                onChange={(date) => {
                  if (date) {
                    handleSearchClients(date.format("YYYY-MM-DD"));
                  } else {
                    setAvailableClients([]);
                    setClientSearchTerm("");
                  }
                }}
              />
            ) : (
              <Input.Search
                placeholder={t("ClientSegment-SearchClients")}
                onSearch={(value) => handleSearchClients(value, 1)}
                enterButton
                style={{ flex: 1 }}
                value={clientSearchTerm}
                onChange={(e) => setClientSearchTerm(e.target.value)}
              />
            )}
          </div>
        </div>
        
        <Table
          size="small"
          loading={loadingClients}
          dataSource={availableClients}
          rowKey="id"
          pagination={{
            current: clientSearchPage,
            total: clientSearchTotal,
            pageSize: 10,
            showSizeChanger: false,
            onChange: handleClientSearchPagination,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
          }}
          columns={[
            {
              title: t("Common-Identifier"),
              dataIndex: "idNumber",
              key: "idNumber",
            },
            {
              title: t("Common-ClientId"),
              dataIndex: "id",
              key: "id",
            },
            {
              title: t("Common-Name"),
              render: (client: any) => `${client.name || ''} ${client.lastName || ''}`.trim(),
              key: "name",
            },
            {
              title: t("Common-Email"),
              dataIndex: "email",
              key: "email",
            },
            {
              title: t("Common-Actions"),
              key: "actions",
              render: (client: any) => (
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => handleAddClients([client])}
                >
                  {t("Common-Add")}
                </Button>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default ViewClientSegment;
